import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Layout } from "../components/Layout";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatCard, SeverityBadge, ReviewBadge, StatusPill, Money, Variance, VarianceBar } from "../components/badges";
import { num, fmtDate, sevRail } from "../lib/format";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { ChevronRight, Coins, History, ArrowRightLeft } from "lucide-react";
import { AuditEvents } from "../components/AuditEvents";
import { MovementsTable } from "../components/MovementsTable";

const CATEGORY_LABEL = {
  EARNING: "Earnings",
  DEDUCTION: "Deductions",
  TAX: "Taxes",
  TAXABLE_BENEFIT: "Taxable Benefits",
  EMPLOYER: "Employer Contributions",
};

const groupByCategory = (lines) => {
  const grouped = {};
  for (const ln of lines) {
    const cat = ln.code_category || "OTHER";
    (grouped[cat] = grouped[cat] || []).push(ln);
  }
  return grouped;
};

export default function EmployeeDetail() {
  const { runId, employeeId } = useParams();
  const [sp] = useSearchParams();
  const ref = sp.get("ref");
  const checkParam = sp.get("check");
  const { data, isLoading, error } = useQuery({
    queryKey: ["emp", runId, employeeId],
    queryFn: async () => (await api.get(`/runs/${runId}/employees/${employeeId}`)).data,
  });

  if (isLoading)
    return (
      <Layout>
        <Skeleton className="h-8 w-72" />
        <Skeleton className="mt-6 h-96 rounded-md" />
      </Layout>
    );

  if (error)
    return (
      <Layout>
        <div className="rounded-md border border-neg/30 bg-neg/5 p-6 text-neg">
          {error.response?.status === 403 ? "You do not have access to this customer." : "Failed to load employee."}
        </div>
      </Layout>
    );

  const { run, employee, findings, amount_lines, movements, has_comparison } = data;
  const ccy = run.currency_code;
  const maxLineDelta = Math.max(1, ...amount_lines.map((l) => Math.abs(l.delta_amount || 0)));
  const failCount = findings.filter((f) => f.result_status === "FAIL").length;
  const grouped = groupByCategory(amount_lines);

  return (
    <Layout>
      <Breadcrumbs
        items={[
          { label: "Portfolio", to: "/" },
          { label: run.client_name, to: `/clients/${run.payroll_client_GUID || ""}` },
          ref === "errors"
            ? { label: "Error Details", to: `/runs/${runId}/errors${checkParam ? `?check=${encodeURIComponent(checkParam)}` : ""}` }
            : { label: `Register ${run.run_label}`, to: `/runs/${runId}` },
          { label: employee.employee_name_as_printed },
        ]}
      />

      <div className="mt-4 border-b border-border pb-5">
        <h1 className="font-heading text-4xl font-black">
          {employee.employee_name_as_printed}
          {employee.is_void ? (
            <span data-testid="emp-void-badge" className="ml-3 align-middle rounded border border-neg/40 bg-neg/10 px-2 py-0.5 text-xs font-bold text-neg">
              VOID
            </span>
          ) : null}
        </h1>
        <p className="mt-1 font-mono text-sm uppercase tracking-wide text-muted-foreground">
          ID {employee.provider_employee_id} · {employee.department_as_printed || "No department"} · Pay date {fmtDate(run.pay_date)}
          {employee.delivery_method ? ` · ${employee.delivery_method.replace(/_/g, " ")}` : ""}
        </p>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="emp-gross" label="Gross Pay" value={<Money value={employee.gross_pay_amount} ccy={ccy} />} bar="bg-sky-500/60" />
        <StatCard testid="emp-net" label="Net Pay" value={<Money value={employee.net_pay_amount} ccy={ccy} />} bar="bg-emerald-500/60" />
        <StatCard testid="emp-hours" label="Total Hours" count={employee.total_hours} decimals={2} bar="bg-foreground/30" />
        <StatCard
          testid="emp-issues"
          label="Findings"
          count={failCount}
          accent={failCount > 0 ? "text-neg" : "text-pos"}
          bar={failCount > 0 ? "bg-neg" : "bg-pos"}
        />
      </div>

      {/* Findings */}
      <section className="mt-8">
        <h2 className="font-heading text-lg font-bold mb-3">Variance Findings</h2>
        {findings.length === 0 ? (
          <div className="rounded-md border border-border bg-card p-6 text-muted-foreground">
            No checks recorded for this employee in this register.
          </div>
        ) : (
          <div className="rounded-md border border-border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Status</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Check</TableHead>
                  <TableHead className="text-right">Observed</TableHead>
                  <TableHead className="text-right">Prior</TableHead>
                  <TableHead className="text-right">Delta</TableHead>
                  <TableHead>Review</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {findings.map((f) => (
                  <TableRow key={f.check_result_GUID} className={`hover:bg-muted/40 border-l-2 ${f.result_status === "FAIL" ? sevRail(f.severity) : "border-l-transparent"}`}>
                    <TableCell><StatusPill status={f.result_status} /></TableCell>
                    <TableCell>{f.result_status === "FAIL" ? <SeverityBadge severity={f.severity} /> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                    <TableCell>
                      <div className="font-medium">{f.check_name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{f.check_code}</div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{num(f.observed_value, 2)}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{num(f.prior_value, 2)}</TableCell>
                    <TableCell className="text-right"><Variance delta={f.delta_value} /></TableCell>
                    <TableCell><ReviewBadge status={f.review_status} /></TableCell>
                    <TableCell className="text-right">
                      <Link
                        to={`/findings/${f.check_result_GUID}`}
                        className="inline-flex items-center text-sm font-semibold text-muted-foreground hover:text-foreground"
                      >
                        Root cause <ChevronRight size={16} />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {/* Pay lines with prior comparison */}
      <section className="mt-10">
        <div className="flex items-center gap-2 mb-3">
          <Coins size={18} className="text-muted-foreground" />
          <h2 className="font-heading text-lg font-bold">Pay Register Detail — What Drove It</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Earning, deduction and tax lines{has_comparison ? ", compared against the prior register" : ""}.
        </p>
        <div className="space-y-6">
          {Object.keys(grouped).sort().map((cat) => (
            <div key={cat} className="rounded-md border border-border bg-card overflow-hidden">
              <div className="bg-muted/50 px-4 py-2 font-heading text-sm font-bold uppercase tracking-wide">
                {CATEGORY_LABEL[cat] || cat}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    {has_comparison && <TableHead className="text-right">Prior</TableHead>}
                    {has_comparison && <TableHead className="text-right">Delta</TableHead>}
                    <TableHead className="text-right">YTD</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grouped[cat].map((ln, i) => (
                    <TableRow key={`${ln.native_code}-${i}`} className="hover:bg-muted/40">
                      <TableCell className="font-mono text-xs">{ln.native_code}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {ln.canonical_code_name || ln.canonical_code || "—"}
                        {ln.jurisdiction_level && ln.jurisdiction_level !== "NONE" ? (
                          <span data-testid="jurisdiction-chip" className="ml-1.5 rounded border border-border bg-muted px-1 py-0.5 text-[9px] font-mono uppercase text-foreground/70">
                            {ln.jurisdiction_state_code || ln.jurisdiction_local_code || ln.jurisdiction_level}
                          </span>
                        ) : null}
                        {ln.is_imputed_income ? (
                          <span data-testid="imputed-flag" title="Imputed income — inside gross but never disbursed" className="ml-1.5 rounded border border-violet-400/40 bg-violet-500/10 px-1 py-0.5 text-[9px] font-mono uppercase text-violet-500">
                            imputed
                          </span>
                        ) : null}
                        {ln.amount_reference ? (
                          <span data-testid="distribution-account" title="Direct-deposit / distribution account" className="ml-1.5 rounded border border-sky-400/40 bg-sky-500/10 px-1 py-0.5 text-[9px] font-mono uppercase text-sky-500">
                            {ln.amount_reference}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right font-mono">{ln.hours != null ? num(ln.hours, 2) : "—"}</TableCell>
                      <TableCell className="text-right"><Money value={ln.amount} ccy={ccy} /></TableCell>
                      {has_comparison && <TableCell className="text-right font-mono text-muted-foreground">{ln.prior_amount != null ? num(ln.prior_amount, 2) : "—"}</TableCell>}
                      {has_comparison && (
                        <TableCell className="text-right">
                          {ln.delta_amount ? (
                            <div className="flex flex-col items-end gap-1">
                              <Variance delta={ln.delta_amount} />
                              <VarianceBar value={ln.delta_amount} max={maxLineDelta} className="w-16" />
                            </div>
                          ) : (
                            <span className="font-mono text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="text-right font-mono text-muted-foreground">{ln.ytd_amount != null ? num(ln.ytd_amount, 2) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
          {amount_lines.length === 0 && (
            <div className="rounded-md border border-border bg-card p-6 text-muted-foreground">
              No pay register line detail available for this employee.
            </div>
          )}
        </div>
      </section>

      {/* What Moved — this employee's period-over-period movements */}
      <section className="mt-10" data-testid="emp-movements-section">
        <div className="flex items-center gap-2 mb-3">
          <ArrowRightLeft size={18} className="text-muted-foreground" />
          <h2 className="font-heading text-lg font-bold">What Moved — vs Prior Register</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Reportable earning, deduction, tax and mileage movements for this employee between the two registers.
        </p>
        <MovementsTable movements={movements || []} ccy={ccy} testid="emp-movements" />
      </section>

      {/* Employee audit trail */}
      <section className="mt-10">
        <div className="flex items-center gap-2 mb-3">
          <History size={18} className="text-muted-foreground" />
          <h2 className="font-heading text-lg font-bold">Employee Audit Trail — Who Changed What</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Master-data changes recorded for this employee. Click an event to see field-level before/after values.
        </p>
        <AuditEvents
          clientId={run.payroll_client_GUID}
          employeeId={employeeId}
          emptyText="No recorded master-data changes for this employee."
        />
      </section>
    </Layout>
  );
}
